import { useContext, useEffect, useState, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { Grid, Flexbox, Divider, Button } from '@sede-enterprise/shell-ds-react-framework';
import 'leaflet/dist/leaflet.css';
import { PptFilterSection } from './PptFilterSection';
import { AppContext } from '../../../../Context/AppContext';
import NcfReportingHomeHeader from '../NcfReportingUtils/NcfReportingHomeHeader/NcfReportingHomeHeader';
import PptReportingBarChart from './PptReportingCharts/PptReportingBarChart/PptReportingBarChart';
import { PptInScopeNcfCatgChart } from './PptReportingCharts/PptReportingPieChart/PptInScopeNcfCatg';
import { PptInScopeVsOutScope } from './PptReportingCharts/PptReportingPieChart/PptInScopeVsOutScope';
import {
  pptNcfCategory,
  pptNcfVolume,
  pptReports,
  pptInScopeVsOutScopeVolume,
} from '../../../../api/services';
import Loader from '../../../Loader/Loader';
import { ASIA_COUNTRIES } from '../../../../api/constants';
import { PPASelectFormField } from '../../PPAInsights/PPAInsightFilterSection/PPASelectFormField';
import { StyledContainer, StyledLabel } from './PptReportingHome.style';
import { globalApiErrorHandler } from '../../../Utils/utils';

export const PptReportingHome = () => {
  const { state: appDataState, dispatch: appDataDispatch } = useContext(AppContext).appData;
  const { state: pptState, dispatch: pptDispatch } = useContext(AppContext).pptReports;
  const userRegion = localStorage.getItem('userRegion') ?? appDataState.userRegion;
  const [isLoading, setIsLoading] = useState(true);
  const isAsiaUser =
    localStorage.getItem('isAsiaUser') !== null
      ? localStorage.getItem('isAsiaUser') === 'true'
      : appDataState.userRegionFlags.isAsiaUser;
  const isRegionSpecificUser = Object.values(appDataState.userRegionFlags).some((el) => el);
  const prevFilteredValuesRef = useRef(pptState.PptfilteredValues);
  const [reportSelected, setReportSelected] = useState<string[]>([]);
  const snapshotReports = pptState.PptfilteredValues.snapshotReports ?? [];
  const [manualTrigger, setManualTrigger] = useState(false);

  useEffect(() => {
    appDataDispatch({ type: 'setCurrentPage', payload: '' });
  }, [appDataDispatch]);

  useEffect(() => {
    if (appDataState.userRegion !== '') {
      let payloadRegion = [appDataState.userRegion];
      if (isAsiaUser) {
        payloadRegion = pptState.PptfilteredValues.region?.length
          ? pptState.PptfilteredValues.region
          : ASIA_COUNTRIES;
      }
      pptDispatch({
        type: 'setPptFilteredValues',
        payload: {
          ...pptState.PptfilteredValues,
          region: payloadRegion,
        },
      });
    }
  }, [appDataState.userRegion, isAsiaUser]);

  // Fetch data when filter values change for all roles
  useEffect(() => {
    if (!isEqual(prevFilteredValuesRef.current, pptState.PptfilteredValues)) {
      fetchData();
      prevFilteredValuesRef.current = pptState.PptfilteredValues;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // Mark that generate data button has been clicked
    setReportSelected(snapshotReports ?? []);
    if (!manualTrigger) {
      setReportSelected([]);
    } else {
      setManualTrigger(false); // Reset the flag after handling} }
    }
  }, [pptState.PptfilteredValues]);

  // For Global role: fetch data on mount
  useEffect(() => {
    if (!isRegionSpecificUser) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { region } = pptState.PptfilteredValues;
      let pptNcfOptions = { ...pptState.PptfilteredValues };

      if (userRegion !== '') {
        let optionRegion = [userRegion];
        if (isAsiaUser) {
          optionRegion = ASIA_COUNTRIES;
        }
        const payloadRegion = region && region.length > 0 ? region : optionRegion;
        pptNcfOptions = { ...pptNcfOptions, region: payloadRegion };
      }
      const volumeResponse = await pptNcfVolume(pptNcfOptions);
      const categoryResponse = await pptNcfCategory(pptNcfOptions);
      const pptInScopeVsOutScopeVolumeResponse = await pptInScopeVsOutScopeVolume(pptNcfOptions);

      pptDispatch({ type: 'setPptNcfVolumes', payload: volumeResponse?.data });
      pptDispatch({
        type: 'setPptNcfCategories',
        payload: categoryResponse?.data,
      });
      pptDispatch({
        type: 'setPptInScopeVsOutScopeVolume',
        payload: pptInScopeVsOutScopeVolumeResponse?.data,
      });
    } catch (error) {
      globalApiErrorHandler('Error fetching PPT data', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Only fetch report names once on mount
  useEffect(() => {
    const fetchReportNames = async () => {
      setIsLoading(true);
      try {
        let pptOptions = { ...pptState.PptfilteredValues };
        if (userRegion !== '') {
          let optionRegion = [userRegion];
          if (isAsiaUser) {
            optionRegion = ASIA_COUNTRIES;
          }
          pptOptions = { ...pptOptions, region: optionRegion };
        }
        const reportsResponse = await pptReports(pptOptions);
        pptDispatch({
          type: 'setPptReportNames',
          payload: reportsResponse?.data,
        });
      } catch (error) {
        globalApiErrorHandler('Error fetching PPT report names', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReportNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setReportSelected(snapshotReports ?? []);
  }, []);

  const hasPower = snapshotReports.some((report) => report.includes('_PW_'));
  const hasNaturalGas = snapshotReports.some((report) => report.includes('_NG_'));
  const showBoth = snapshotReports.length === 0 || (hasPower && hasNaturalGas);
  const options = pptState.pptReportNames.map((reportName) => reportName);
  const [isDataGenerated, setIsDataGenerated] = useState(false); // New state
  const handleGenerateData = () => {
    // Mocking data generation based on the selected report
    if (reportSelected) {
      const payload = {
        ...pptState.PptfilteredValues,
        snapshotReports: [...reportSelected],
      };
      setManualTrigger(true); // Mark this as a manual trigger
      pptDispatch({ type: 'setPptFilteredValues', payload });
      if (reportSelected.length > 0) {
        setIsDataGenerated(true);
      }
    }
  };
  useEffect(() => {
    if (reportSelected.length === 0) {
      setIsDataGenerated(false);
    }
  }, [reportSelected]);
  const snapshotCondition = isDataGenerated && reportSelected.length > 0;

  return (
    <div style={{ height: '100%', background: '#F5F5F5' }} data-testid='report-breakdown'>
      {isLoading && <Loader loader={true} message={'Loading...'} />}

      <Flexbox gap='24px 24px' style={{ marginLeft: '15px', justifyContent: 'start' }}>
        <PptFilterSection />
        <Grid gap='10px'>
          <Grid.Cell>
            <Flexbox justifyContent='space-between'>
              <NcfReportingHomeHeader
                title='Report Breakdown'
                description='Detailed Report breakdown for Historic Gas and Power Volumes'
              />
            </Flexbox>
            <Divider size='medium' direction='horizontal' style={{ marginTop: '20px' }} />
            <div data-testid='filter' style={{ width: '100%', marginTop: '20px' }}>
              <Flexbox flexDirection='row' alignItems='center' gap='20px' style={{ width: '60%' }}>
                <PPASelectFormField
                  id='report'
                  label='Select the report to view data'
                  testId='Report'
                  value={reportSelected}
                  onChange={setReportSelected}
                  options={options}
                  mode='multiple'
                />
                <Button
                  className='getDataBtn'
                  data-testid='Generate Data'
                  onClick={handleGenerateData}
                  style={{
                    marginTop: '9px',
                    width: '25%',
                  }}
                  size='medium'
                >
                  Generate Data
                </Button>
              </Flexbox>
              {!isDataGenerated && (
                <StyledContainer>
                  <StyledLabel>Please select a data snapshot to visualise the report</StyledLabel>
                </StyledContainer>
              )}
            </div>
            {snapshotCondition && <PptReportingBarChart />}
          </Grid.Cell>
          <Grid.Cell>
            {isDataGenerated && (
              <Flexbox gap='10px'>
                {showBoth || hasNaturalGas ? (
                  <PptInScopeVsOutScope tradeName='Natural Gas' />
                ) : null}
                {showBoth || hasPower ? <PptInScopeVsOutScope tradeName='Power' /> : null}
              </Flexbox>
            )}
          </Grid.Cell>
          <Grid.Cell>
            {isDataGenerated && (
              <Flexbox gap='10px'>
                {showBoth || hasNaturalGas ? (
                  <PptInScopeNcfCatgChart tradeName='Natural Gas' />
                ) : null}
                {showBoth || hasPower ? <PptInScopeNcfCatgChart tradeName='Power' /> : null}
              </Flexbox>
            )}
          </Grid.Cell>
        </Grid>
      </Flexbox>
    </div>
  );
};
